import { useCallback, useContext, useEffect, useState } from "react";

import { Checkbox, CheckboxValue, Textbox, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { AuthenticationContext } from "@/context";
import { getPACaseList } from "@/api";
import { Body, Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Button, Icon, Spinner } from "capitalrx-components-2.0/lib/general";
import PAResultList from "@/components/PAListForm/PAResultList";

import styles from "./PACaseListSearch.module.scss";
import { getThemeURL } from "@/utils";
import { useNavigate } from "react-router";

interface PACaseListSearchProps {
  pageNumber: number;
  setPageNumber: (page: number) => void;
  externalMemberID: string[];
  patientDOB?: string;
}
export const PACaseListSearch = (props: PACaseListSearchProps) => {

  // Props
  const { pageNumber, setPageNumber, externalMemberID, patientDOB } = props;

  // Context
  const { isUserLoggedIn, getAuthToken } = useContext(AuthenticationContext);
  const navigate = useNavigate();

  // useSimpleInputState initializers
  const [actionRequiredValue, onChangeActionRequired] = useSimpleInputState<CheckboxValue>(false);
  const [searchResults, onChangeSearch] = useSimpleInputState<string>("");

  // useState initializers
  const [loading, setLoading] = useState<boolean>(false);
  const [paResults, setPAResults] = useState([]);
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([]);
  const [filteredPAResults, setFilteredPAResults] = useState(paResults);

  // Mappings
  const outcomeMap: Record<string, string> = {
    "In Progress": "in_progress",
    "Approved": "approved",
    "Denied": "denied",
  };

  // Handlers to update filter states

  const toggleOutcome = (label: string) => {
    const normalized = outcomeMap[label];
    setSelectedOutcomes((prev) => {
      // If the clicked outcome is already selected, clear it
      if (prev.includes(normalized)) {
        return [];
      }
      // Otherwise, replace with the new single outcome
      return [normalized];
    });
  };

  const handleActionRequired = useCallback((value: CheckboxValue, prev: CheckboxValue) => {
    onChangeActionRequired(value, prev);
  }, [onChangeActionRequired]);

  const handleChange = useCallback((value: string, prev: string) => {
    onChangeSearch(value, prev);
  }, [onChangeSearch]);

  // Helper function to normalize case status based on case completion

  const normalizeOutcome = (result: any): string => {
    if (!result.case.is_complete) {
      return "in_progress"; // override everything if incomplete
    }

    const rawOutcome = result.case_outcome?.outcome;
    return !rawOutcome || rawOutcome === "closed" ? "in_progress" : rawOutcome;
  };

  // Apply all filters whenever any dependency changes

  useEffect(() => {
    const results = paResults.filter((result) => {
      const outcome = normalizeOutcome(result);

      // Outcome filter (single selection)
      if (selectedOutcomes.length > 0 && outcome !== selectedOutcomes[0]) {
        return false;
      }

      // Action Required filter
      if (actionRequiredValue && result.case.queue !== "outreach") return false;

      // Search filter
      if (searchResults.trim().length > 0) {
        const idMatch = result.case.case_id.toString().includes(searchResults);
        const drugMatch = result.case.drug_name.toLowerCase().includes(searchResults.toLowerCase());
        if (!idMatch && !drugMatch) return false;
      }

      return true;
    });

    setFilteredPAResults(results);
  }, [paResults, actionRequiredValue, searchResults, selectedOutcomes]);

  useEffect(() => {
    const fetchCases = async () => {
      if (isUserLoggedIn()) {
        setLoading(true);
        try {
          const authToken = getAuthToken();
          const requestPayload = {
            is_action_required: false,
            results_per_page: 144,
            page_number: pageNumber,
            external_member_id: externalMemberID,
          };

          const response = await getPACaseList(requestPayload, authToken);
          setPAResults(response?.results);
          setFilteredPAResults(response?.results);
        } catch (error) {
          console.error("Failed to fetch cases:", error);
        } finally {
          setLoading(false);
        }
      };
    };

    fetchCases();
  }, [pageNumber, isUserLoggedIn]);

  // Reverse mapping: value -> label
  const labelByValue: Record<string, string> = Object.fromEntries(Object.entries(outcomeMap).map(([label, value]) => [value, label]));

  const selectedLabels =
    selectedOutcomes?.length
      ? selectedOutcomes.map((value) => labelByValue[value] ?? value) // fallback to raw value if not found
      : ["all"];


  return (<>
    {!loading &&

      <Stack>
        <Shelf>
          <Subtitle>PA Case List</Subtitle>
          <Shelf.Spacer />
          <Button appearance="secondary" onClick={() => navigate(getThemeURL("PA_CREATE"))}>Start A New Case</Button>
        </Shelf>
        <Textbox
          prefix={<Icon name="search" size="small" />}
          hideLabel={true}
          placeholder="Search"
          onChange={handleChange}
          value={searchResults}
          instructions="Search by case ID or drug name"
          {...props}
        />
        <Shelf>
          <Stack spacing="mini">
            <Paragraph className={styles.SectionText}>filter by status</Paragraph>
            <Stack.Spacer />
            <Shelf spacing="small">
              {Object.keys(outcomeMap).map((label) => {
                const normalized = outcomeMap[label];
                return (
                  <>
                    <button
                      key={label}
                      onClick={() => toggleOutcome(label)}
                      className={selectedOutcomes.includes(normalized) ? styles.StatusFilterActive : styles.StatusFilter}
                    >
                      {label}
                    </button>
                  </>
                );
              })}
            </Shelf>
          </Stack>
          <Shelf.Spacer />
          <Checkbox label="Only show cases with Action Required" onChange={handleActionRequired} value={actionRequiredValue} />
        </Shelf >
        <Stack spacing="large">
          <Stack.Spacer />
        </Stack>
        <body className={styles.SectionText}>{`${(selectedLabels?.join(", ") || "all")} cases`.toLowerCase()}</body>
        {loading && (
          <Stack className={styles.LoadingContainer} alignment="center">
            <Spinner className={styles.LoadingSpinner} size="large" />
          </Stack>
        )}
        {!loading && paResults?.length === 0 && <Body>Member has no prior authorizations</Body>}
        {!loading && paResults && <PAResultList results={filteredPAResults} page={pageNumber} setPage={setPageNumber} singleMember={true} patientDOB={patientDOB} />}
      </Stack >
    }
  </>
  );
};
